import supabase from "../../supabase/supabase";

const PROFILE_COLUMNS =
  "id,first_name,last_name,email,phone,avatar_url,role,created_at,updated_at";

const CLIENT_PROFILE_COLUMNS = [
  "company_name",
  "business_type",
  "industry",
  "business_email",
  "phone",
  "business_address",
  "year_started",
  "monthly_revenue",
  "employee_count",
  "accepts_online_payments",
  "bookkeeping_manager",
  "uses_software",
  "country",
  "currency",
  "fiscal_year",
  "tax_id",
].join(",");

const BUSINESS_PROFILE_FIELDS = new Set([
  "company_name",
  "business_type",
  "industry",
  "business_email",
  "phone",
  "business_address",
  "year_started",
  "monthly_revenue",
  "employee_count",
  "accepts_online_payments",
  "bookkeeping_manager",
  "uses_software",
]);

const getAuthenticatedUser = async (expectedUserId) => {
  const { data, error } = await supabase.auth.getUser();

  if (error || !data?.user) {
    throw new Error(
      error?.message ||
        "Your session has expired. Please sign in again."
    );
  }

  if (
    expectedUserId &&
    data.user.id !== expectedUserId
  ) {
    throw new Error(
      "The authenticated user changed. Reload Settings and try again."
    );
  }

  return data.user;
};

const readSingleRow = async (query, label) => {
  const { data, error } = await query;

  if (error) {
    throw new Error(`${label}: ${error.message}`);
  }

  return data || null;
};

const getAuthDetails = (user) => {
  const identities = Array.isArray(user.identities)
    ? user.identities
    : [];
  const providers = [
    ...new Set(
      identities
        .map((identity) => identity.provider)
        .filter(Boolean)
    ),
  ];
  const primaryProvider =
    user.app_metadata?.provider ||
    providers[0] ||
    "Unknown";

  return {
    id: user.id,
    email: user.email || "",
    emailVerified: Boolean(user.email_confirmed_at),
    provider: primaryProvider,
    providers,
    supportsPassword: providers.includes("email") ||
      primaryProvider === "email",
  };
};

export const getClientSettings = async ({
  organizationId,
  userId,
}) => {
  if (!organizationId) {
    throw new Error("An active client organization is required.");
  }

  const user = await getAuthenticatedUser(userId);

  const membership = await readSingleRow(
    supabase
      .schema("accounting")
      .from("organization_users")
      .select("organization_id,user_id,role")
      .eq("organization_id", organizationId)
      .eq("user_id", user.id)
      .maybeSingle(),
    "Unable to verify your organization membership"
  );

  if (!membership) {
    throw new Error(
      "Your account is not authorized for this client organization."
    );
  }

  const organization = await readSingleRow(
    supabase
      .schema("accounting")
      .from("organizations")
      .select("id,name,type,country,currency,fiscal_year,website")
      .eq("id", organizationId)
      .eq("type", "client")
      .maybeSingle(),
    "Unable to load the business organization"
  );

  if (!organization) {
    throw new Error(
      "The selected client organization could not be found."
    );
  }

  const [clientProfile, profile] = await Promise.all([
    readSingleRow(
      supabase
        .schema("accounting")
        .from("client_profiles")
        .select(CLIENT_PROFILE_COLUMNS)
        .eq("organization_id", organizationId)
        .maybeSingle(),
      "Unable to load the business profile"
    ),
    readSingleRow(
      supabase
        .from("profiles")
        .select(PROFILE_COLUMNS)
        .eq("id", user.id)
        .maybeSingle(),
      "Unable to load your personal profile"
    ),
  ]);

  return {
    organizationId,
    organization,
    clientProfile,
    profile,
    membershipRole: membership.role,
    canEditBusiness: membership.role === "owner",
    auth: getAuthDetails(user),
  };
};

const cleanBusinessChanges = (changes) => {
  const result = {};

  for (const [field, value] of Object.entries(changes || {})) {
    if (!BUSINESS_PROFILE_FIELDS.has(field)) continue;

    if (field === "year_started") {
      if (value === "" || value == null) {
        result[field] = null;
        continue;
      }

      const year = Number(value);
      const thisYear = new Date().getFullYear();

      if (
        !Number.isInteger(year) ||
        year < 1800 ||
        year > thisYear
      ) {
        throw new Error(
          `Year started must be a whole number between 1800 and ${thisYear}.`
        );
      }

      result[field] = year;
      continue;
    }

    if (
      field === "accepts_online_payments" ||
      field === "uses_software"
    ) {
      result[field] = value === true;
      continue;
    }

    result[field] =
      typeof value === "string" ? value.trim() : value;
  }

  if (
    result.business_email &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      result.business_email
    )
  ) {
    throw new Error("Enter a valid business email address.");
  }

  return result;
};

const cleanWebsite = (value) => {
  const website = typeof value === "string"
    ? value.trim()
    : "";

  if (!website) return "";

  const normalized = /^https?:\/\//i.test(website)
    ? website
    : `https://${website}`;

  try {
    const url = new URL(normalized);

    if (!["http:", "https:"].includes(url.protocol)) {
      throw new Error();
    }

    return url.href;
  } catch {
    throw new Error("Enter a valid website URL.");
  }
};

export const updateClientBusinessProfile = async ({
  organizationId,
  userId,
  organizationChanges = {},
  clientProfileChanges = {},
}) => {
  const current = await getClientSettings({
    organizationId,
    userId,
  });

  if (!current.canEditBusiness) {
    throw new Error(
      "Only an owner of this client organization can edit its business profile."
    );
  }

  const organizationUpdates = {};

  if (Object.hasOwn(organizationChanges, "name")) {
    const name = organizationChanges.name.trim();

    if (!name) {
      throw new Error("Business name is required.");
    }

    organizationUpdates.name = name;
  }

  if (Object.hasOwn(organizationChanges, "website")) {
    organizationUpdates.website = cleanWebsite(
      organizationChanges.website
    );
  }

  const clientProfileUpdates =
    cleanBusinessChanges(clientProfileChanges);

  let organization = current.organization;
  let clientProfile = current.clientProfile;

  if (Object.keys(organizationUpdates).length) {
    organization = await readSingleRow(
      supabase
        .schema("accounting")
        .from("organizations")
        .update(organizationUpdates)
        .eq("id", organizationId)
        .eq("type", "client")
        .select("id,name,type,country,currency,fiscal_year,website")
        .maybeSingle(),
      "Unable to save organization details"
    );

    if (!organization) {
      throw new Error(
        "The organization was not updated. Check your organization permissions."
      );
    }
  }

  if (Object.keys(clientProfileUpdates).length) {
    if (!current.clientProfile) {
      throw new Error(
        "There is no business profile to update."
      );
    }

    clientProfile = await readSingleRow(
      supabase
        .schema("accounting")
        .from("client_profiles")
        .update(clientProfileUpdates)
        .eq("organization_id", organizationId)
        .select(CLIENT_PROFILE_COLUMNS)
        .maybeSingle(),
      "Unable to save business profile details"
    );

    if (!clientProfile) {
      throw new Error(
        "The business profile was not updated. Check your organization permissions."
      );
    }
  }

  return {
    ...current,
    organization,
    clientProfile,
  };
};

export const updateOwnProfile = async ({
  userId,
  changes = {},
}) => {
  const user = await getAuthenticatedUser(userId);
  const allowed = {};

  for (const field of ["first_name", "last_name", "phone"]) {
    if (Object.hasOwn(changes, field)) {
      allowed[field] =
        typeof changes[field] === "string"
          ? changes[field].trim()
          : "";
    }
  }

  if (!Object.keys(allowed).length) {
    throw new Error("No supported profile changes were provided.");
  }

  const { data, error } = await supabase
    .from("profiles")
    .update(allowed)
    .eq("id", user.id)
    .select(PROFILE_COLUMNS)
    .maybeSingle();

  if (error) {
    throw new Error(`Unable to save your profile: ${error.message}`);
  }

  if (!data) {
    throw new Error(
      "Your profile was not updated. Check that you are signed in."
    );
  }

  return data;
};

export const updateOwnPassword = async ({
  userId,
  password,
}) => {
  const user = await getAuthenticatedUser(userId);
  const details = getAuthDetails(user);

  if (!details.supportsPassword) {
    throw new Error(
      "Password changes are unavailable for this sign-in provider."
    );
  }

  if (
    typeof password !== "string" ||
    password.length < 8 ||
    !/[a-z]/.test(password) ||
    !/[A-Z]/.test(password) ||
    !/[0-9]/.test(password)
  ) {
    throw new Error(
      "Use at least 8 characters with an uppercase letter, a lowercase letter, and a number."
    );
  }

  const { data, error } = await supabase.auth.updateUser({
    password,
  });

  if (error) {
    throw new Error(
      `Unable to change your password: ${error.message}`
    );
  }

  if (!data?.user) {
    throw new Error(
      "Supabase did not confirm the password update."
    );
  }

  return true;
};